import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import Dexie from 'dexie';
import { phoneticMap } from './searchConfig';

// Инициализация Dexie
const db = new Dexie('BitrixElementsCache');
db.version(2).stores({
  items: 'key, data, timestamp',
  wordIndex: 'key, data, timestamp, itemsHash'
});

const CACHE_KEY = 'bitrix_elements_cache';
const WORD_INDEX_KEY = 'word_index_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 часа

const latinTranslitMap = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo', 'ж': 'zh', 'з': 'z',
  'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r',
  'с': 's', 'т': 't', 'у': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
  'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
  'скандик': 'skandik',
};

// Кэшируем регулярные выражения
const osbRegex = /осб|осп|osb/g;
const penoplexRegex = /пеноплекс|пеноплэкс/g;
const parensRegex = /[()]/g;
const spaceRegex = /\s+/g;
const commaRegex = /,/g;
const dotRegex = /\./g;
const numberPattern = /\d+[.,]\d+/g;

const transliterateToLatin = (input) => {
  if (!input) return '';
  return input
    .toLowerCase()
    .replace(/скандик/g, 'skandik')
    .split('')
    .map((char) => latinTranslitMap[char] || char)
    .join('');
};

const applyPhoneticMapping = (input) => {
  if (!input) return '';
  const lower = input.toLowerCase();
  return phoneticMap[lower] || input; // Убрали частичный поиск для скорости
};

const normalizeOsbText = (text) => {
  if (!text || typeof text !== 'string') return { primary: '', alternate: '' };
  const normalized = text
    .toLowerCase()
    .replace(osbRegex, 'osb')
    .replace(penoplexRegex, 'пеноплэкс')
    .replace(parensRegex, '')
    .replace(spaceRegex, ' ')
    .trim();
  const primary = normalized.replace(commaRegex, '.');
  return { primary }; // Убрали alternate для оптимизации
};

const normalizeWordForIndex = (word) => {
  if (!word) return [];
  return [word.replace(commaRegex, '.')]; // Убрали withComma
};

const extractNumberSubstrings = (word) => {
  if (!word) return [];
  const matches = word.match(numberPattern) || [];
  return matches.flatMap(match => normalizeWordForIndex(match));
};

// Предобработка данных
const preprocessItem = (item) => {
  const names = [item.name, item.name_bitrix].filter(Boolean);
  const processed = {};
  names.forEach((name, index) => {
    const normalized = normalizeOsbText(name.toLowerCase());
    const latin = transliterateToLatin(normalized.primary);
    const prefix = index === 0 ? '' : 'Bitrix';
    processed[`normalizedName${prefix}`] = normalized.primary;
    processed[`latinName${prefix}`] = latin;
  });
  return { ...item, ...processed };
};

export const CacheProvider = ({ children }) => {
  const [cache, setCache] = useState([]);
  const [wordIndex, setWordIndex] = useState(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalCount, setTotalCount] = useState(Infinity);

  const createItemsHash = useCallback((items) => {
    const sample = [...items.slice(0, 100), ...items.slice(-100)];
    return items.length + '-' + sample.map(i => i.name?.length || 0).join('');
  }, []);

  const saveWordIndex = useCallback(async (index, itemsHash) => {
    try {
      const indexObject = Object.fromEntries(index);
      await db.wordIndex.put({
        key: WORD_INDEX_KEY,
        data: indexObject,
        timestamp: Date.now(),
        itemsHash
      });
      console.log('💾 WordIndex сохранен в кэш');
    } catch (error) {
      console.error('❌ Ошибка сохранения wordIndex:', error);
    }
  }, []);

  const loadWordIndex = useCallback(async (currentItemsHash) => {
    try {
      const cached = await db.wordIndex.get(WORD_INDEX_KEY);
      if (!cached) {
        console.log('📂 WordIndex не найден в кэше');
        return null;
      }
      const age = Date.now() - cached.timestamp;
      if (age > CACHE_TTL || cached.itemsHash !== currentItemsHash) {
        console.log(age > CACHE_TTL ? '⏰ WordIndex устарел' : '🔄 Список товаров изменился');
        await db.wordIndex.delete(WORD_INDEX_KEY);
        return null;
      }
      const index = new Map(Object.entries(cached.data));
      console.log('✅ WordIndex загружен из кэша:', index.size, 'ключей');
      return index;
    } catch (error) {
      console.error('❌ Ошибка загрузки wordIndex:', error);
      return null;
    }
  }, []);

  const buildWordIndexInWorker = useCallback(async (data, onProgress, startFrom = 0, endAt = null) => {
    return new Promise((resolve, reject) => {
      const workerCode = `
        const phoneticMap = ${JSON.stringify(phoneticMap)};
        const latinTranslitMap = ${JSON.stringify(latinTranslitMap)};
        const osbRegex = /осб|осп|osb/g;
        const penoplexRegex = /пеноплекс|пеноплэкс/g;
        const parensRegex = /[()]/g;
        const spaceRegex = /\\s+/g;
        const commaRegex = /,/g;
        const numberPattern = /\\d+[.,]\\d+/g;

        function applyPhoneticMapping(input) {
          if (!input) return '';
          const lower = input.toLowerCase();
          return phoneticMap[lower] || input;
        }

        function normalizeWordForIndex(word) {
          if (!word) return [];
          return [word.replace(/,/g, '.')];
        }

        function extractNumberSubstrings(word) {
          if (!word) return [];
          const matches = word.match(/\\d+[.,]\\d+/g) || [];
          return matches.flatMap(match => normalizeWordForIndex(match));
        }

        self.onmessage = function({ data: { items, offset } }) {
          const wordIndex = {};

          for (let i = 0; i < items.length; i++) {
            const idx = i + offset;
            const item = items[i];
            const names = [
              item.normalizedName, item.normalizedNameBitrix,
              item.latinName, item.latinNameBitrix
            ].filter(Boolean);

            names.forEach(variant => {
              if (!wordIndex[variant]) wordIndex[variant] = [];
              wordIndex[variant].push(idx);

              const words = variant.split(/\\s+/);
              words.forEach(word => {
                if (word.length >= 3) {
                  normalizeWordForIndex(word).forEach(w => {
                    if (!wordIndex[w]) wordIndex[w] = [];
                    wordIndex[w].push(idx);
                  });

                  const phonetic = applyPhoneticMapping(word);
                  if (phonetic && phonetic !== word && phonetic.length >= 3) {
                    normalizeWordForIndex(phonetic).forEach(w => {
                      if (!wordIndex[w]) wordIndex[w] = [];
                      wordIndex[w].push(idx);
                    });
                  }

                  extractNumberSubstrings(word).forEach(n => {
                    if (!wordIndex[n]) wordIndex[n] = [];
                    wordIndex[n].push(idx);
                  });
                }
              });
            });

            if (i % 1000 === 0) {
              const progress = Math.round((i / items.length) * 100);
              self.postMessage({ type: 'progress', progress });
            }
          }

          self.postMessage({ type: 'complete', wordIndex });
        };
      `;

      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      const worker = new Worker(workerUrl);

      worker.onmessage = function(e) {
        if (e.data.type === 'progress') {
          onProgress?.(e.data.progress);
        } else if (e.data.type === 'complete') {
          URL.revokeObjectURL(workerUrl);
          worker.terminate();
          resolve(new Map(Object.entries(e.data.wordIndex)));
        }
      };

      worker.onerror = function(error) {
        console.error('❌ Ошибка в Worker:', error);
        URL.revokeObjectURL(workerUrl);
        worker.terminate();
        reject(error);
      };

      const slice = data.slice(startFrom, endAt ?? data.length);
      worker.postMessage({ items: slice, offset: startFrom });
    });
  }, []);

  const buildWordIndexParallel = useCallback(async (data, onProgress) => {
    const startTime = performance.now();
    const numWorkers = Math.min(Math.ceil(data.length / 1000), Math.min(navigator.hardwareConcurrency || 2, 4));
    console.log(`🚀 Запускаем ${numWorkers} Workers для ${data.length} товаров...`);

    const chunkSize = Math.ceil(data.length / numWorkers);
    const workerPromises = [];

    for (let i = 0; i < numWorkers; i++) {
      const startFrom = i * chunkSize;
      const endAt = Math.min((i + 1) * chunkSize, data.length);
      if (startFrom >= data.length) break;

      workerPromises.push(buildWordIndexInWorker(
        data,
        (progress) => onProgress?.(progress, i + 1, numWorkers),
        startFrom,
        endAt
      ));
    }

    const results = await Promise.all(workerPromises);
    console.log(`🔗 Объединяем результаты ${results.length} Workers...`);

    const mergedIndex = new Map();
    results.forEach(workerIndex => {
      workerIndex.forEach((indices, key) => {
        const uniqueIndices = [...new Set([...(mergedIndex.get(key) || []), ...indices])];
        mergedIndex.set(key, uniqueIndices);
      });
    });

    const duration = ((performance.now() - startTime) / 1000).toFixed(2);
    console.log(`✅ Параллельная индексация завершена за ${duration}с:`, mergedIndex.size, 'ключей');
    return mergedIndex;
  }, [buildWordIndexInWorker]);

  const buildWordIndexAsync = useCallback(async (data, onProgress, startFrom = 0) => {
    return startFrom > 0
      ? buildWordIndexInWorker(data, onProgress, startFrom)
      : buildWordIndexParallel(data, onProgress);
  }, [buildWordIndexInWorker, buildWordIndexParallel]);

  const saveCache = useCallback(async (data) => {
    try {
      await db.items.put({ key: CACHE_KEY, data, timestamp: Date.now() });
    } catch (err) {
      console.error('Ошибка сохранения кэша:', err);
    }
  }, []);

  // 🆕 Принудительно обновить кэш с сервера (инкрементально)
  const forceRefreshCache = useCallback(async () => {
    try {
      // Получаем последнее время синхронизации
      const lastSync = sessionStorage.getItem('cacheLastSync');
      const since = lastSync ? lastSync : new Date(Date.now() - 24*60*60*1000).toISOString();

      console.log('🔄 Проверяем обновления с сервера...', { since });

      // Запрашиваем только изменения
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/adminka/bitrix_elements_changes?since=${encodeURIComponent(since)}`,
        { credentials: 'include' }
      );
      const result = await response.json();

      if (!result.success) {
        console.warn('⚠️ API вернул ошибку:', result.error);
        return;
      }

      const newItems = result.data || [];
      console.log(`📦 Получено ${newItems.length} новых товаров с сервера`);

      if (newItems.length === 0) {
        console.log('ℹ️ Новых товаров нет, кэш актуален');
        return;
      }

      // Преобразуем в формат кэша
      const transformedItems = newItems.map(item => preprocessItem({
        value: item.name || 'Без названия',
        label: item.name || 'Без названия',
        bitrixId: item.bitrix_id,
        measure: item.measure || '#N/A',
        name: item.name || 'Без названия',
        category: item.category || '',
        source: item.source || 'bitrix',
      }));

      // СЛИВАЕМ с существующим кэшем (НЕ заменяем!)
      const existingCache = cache;
      const existingIds = new Set(existingCache.map(item => item.bitrixId));
      const trulyNewItems = transformedItems.filter(item => !existingIds.has(item.bitrixId));

      if (trulyNewItems.length === 0) {
        console.log('ℹ️ Все товары уже есть в кэше');
        return;
      }

      console.log(`🆕 Добавляем ${trulyNewItems.length} новых товаров в кэш`);

      const mergedCache = [...existingCache, ...trulyNewItems];
      setCache(mergedCache);

      // Обновляем индекс только для новых элементов
      console.log('🔄 Обновляем поисковый индекс...');
      const newWordIndex = await buildWordIndexInWorker(trulyNewItems, () => {}, 0, trulyNewItems.length);
      const mergedIndex = new Map([...wordIndex, ...newWordIndex]);
      setWordIndex(mergedIndex);

      // Сохраняем в кэш
      const itemsHash = createItemsHash(mergedCache);
      await saveWordIndex(mergedIndex, itemsHash);
      await saveCache(mergedCache);

      // Обновляем время синхронизации
      sessionStorage.setItem('cacheLastSync', new Date().toISOString());

      console.log(`✅ Кэш обновлен: добавлено ${trulyNewItems.length} товаров (всего: ${mergedCache.length})`);

    } catch (err) {
      console.error('❌ Ошибка обновления кэша:', err);
    }
  }, [cache, wordIndex, saveCache, buildWordIndexInWorker, saveWordIndex, createItemsHash]);

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const cached = await db.items.get(CACHE_KEY);
      if (cached && cached.data && cached.timestamp && (Date.now() - cached.timestamp < CACHE_TTL)) {
        const preprocessedData = cached.data;
        setCache(preprocessedData);
        setProgress(30);

        // Сразу даем доступ к данным, продолжаем индексацию в фоне
        setIsInitialLoadComplete(true);
        setIsLoading(false);
        setProgress(100);

        const itemsHash = createItemsHash(preprocessedData);
        
        // Индексация выполняем асинхронно в фоне
        const indexInBackground = async () => {
          try {
            const cachedIndex = await loadWordIndex(itemsHash);
            if (cachedIndex) {
              setWordIndex(cachedIndex);
              console.log('⚡ WordIndex загружен мгновенно из кэша!');
              return;
            }
            console.log('🏗️ WordIndex в кэше отсутствует, строим в фоне...');
            const fullIndex = await buildWordIndexParallel(preprocessedData);
            setWordIndex(fullIndex);
            await saveWordIndex(fullIndex, itemsHash);
            console.log('✅ Фоновая индексация завершена:', fullIndex.size, 'ключей');
          } catch (err) {
            console.error('Ошибка фоновой индексации:', err);
          }
        };
        
        setTimeout(indexInBackground, 10);

        // Фоновое обновление только если кэш старше 4 часов
        const FOUR_HOURS = 4 * 60 * 60 * 1000;
        if (Date.now() - cached.timestamp > FOUR_HOURS) {
          setTimeout(fetchFullDataInBackground, 2000);
        }
      } else {
        console.log('🏗️ Загружаем свежие данные с сервера...');
        
        const response = await fetch(
          `${process.env.REACT_APP_PATH_TO_BACKEND}/api/adminka/initial_index`,
          { credentials: 'include' }
        );
        const result = await response.json();
        if (!result.success) throw new Error(result.error || 'Ошибка загрузки');

        const items = result.data.items.map(item => preprocessItem({
          value: item.name || '',
          label: item.name || '',
          bitrixId: item.bitrixId,
          measure: item.measure || '#N/A',
          name: item.name || '',
          category: item.category || '',
          source: item.source || 'bitrix',
        }));

        setCache(items);
        setWordIndex(new Map(Object.entries(result.data.wordIndex)));
        setTotalCount(result.data.totalCount || 0);
        setIsInitialLoadComplete(true);
        setProgress(100);
        await saveCache(items);
        setTimeout(fetchFullDataInBackground, 0);
      }
    } catch (err) {
      console.error('Ошибка загрузки начальных данных:', err);
    } finally {
      setIsLoading(false);
    }
  }, [saveCache, buildWordIndexParallel, loadWordIndex, saveWordIndex, createItemsHash]);

  const fetchFullDataInBackground = useCallback(async () => {
    // Проверяем, не запущена ли уже фоновая загрузка
    if (window.backgroundLoadingStarted) {
      console.log('🔄 Фоновая загрузка уже запущена, пропускаем');
      return;
    }
    window.backgroundLoadingStarted = true;
    
    try {
      console.log('🔄 Запускаем фоновую загрузку данных с задержкой...');
      
      // Задержка, чтобы не блокировать основной поток
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const response = await fetch(
        `${process.env.REACT_APP_PATH_TO_BACKEND}/api/adminka/bitrix_elements_full_all`,
        { credentials: 'include' }
      );
      const result = await response.json();
      if (!result.success) return;

      const bitrixData = result.data.bitrix.map(item => preprocessItem({
        value: item.name || 'Без названия',
        label: item.name || 'Без названия',
        bitrixId: item.bitrix_id,
        measure: item.measure || '#N/A',
        name: item.name || 'Без названия',
        source: 'bitrix',
      }));

      const sourcesData = result.data.sources.map(item => preprocessItem({
        value: (item.name_bitrix || item.name) || 'Без названия',
        label: (item.name_bitrix || item.name) || 'Без названия',
        bitrixId: item.id_bitrix,
        measure: '#N/A',
        name: item.name || 'Без названия',
        name_bitrix: item.name_bitrix || undefined,
        source: 'sources',
      }));

      const allData = [...bitrixData, ...sourcesData];
      
      // Используем requestIdleCallback для индексации в свободное время
      const indexWithIdleCallback = () => {
        return new Promise((resolve) => {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              buildWordIndexParallel(allData).then(resolve);
            });
          } else {
            setTimeout(() => {
              buildWordIndexParallel(allData).then(resolve);
            }, 0);
          }
        });
      };

      console.log('⚡ Фоновая параллельная индексация для', allData.length, 'товаров');
      const fullIndex = await indexWithIdleCallback();
      setWordIndex(fullIndex);
      console.log('✅ Полный фоновый индекс готов:', fullIndex.size, 'ключей');

      const itemsHash = createItemsHash(allData);
      await saveWordIndex(fullIndex, itemsHash);
      await saveCache(allData);
    } catch (err) {
      console.error('Ошибка фоновой загрузки:', err);
    } finally {
      window.backgroundLoadingStarted = false;
    }
  }, [saveCache, buildWordIndexParallel, saveWordIndex, createItemsHash]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <CacheContext.Provider
      value={{ cache, wordIndex, isLoading, isInitialLoadComplete, progress, totalCount, forceRefreshCache }}
    >
      {children}
    </CacheContext.Provider>
  );
};

export const CacheContext = createContext();
export const useCache = () => useContext(CacheContext);